/**
 * Authentication.
 *  - dev:     the API trusts an `x-dev-user: <userId>` header; the user is picked in the avatar menu ("View as").
 *  - cognito: Cognito Hosted UI, OAuth2 authorization-code flow with PKCE, implemented with fetch + crypto.subtle.
 *             Tokens live in memory and sessionStorage (per tab, cleared when the tab closes).
 */

import { DEFAULT_DEV_USER } from './devUsers';

export type AuthMode = 'dev' | 'cognito';

const env = import.meta.env;

export const authMode: AuthMode =
  env.VITE_AUTH_MODE === 'cognito' || env.VITE_AUTH_MODE === 'dev'
    ? env.VITE_AUTH_MODE
    : env.DEV
      ? 'dev'
      : 'cognito';

/* ---------------- safe storage ---------------- */

function readStore(store: 'local' | 'session', key: string): string | null {
  try {
    return (store === 'local' ? localStorage : sessionStorage).getItem(key);
  } catch {
    return null;
  }
}
function writeStore(store: 'local' | 'session', key: string, value: string | null): void {
  try {
    const s = store === 'local' ? localStorage : sessionStorage;
    if (value === null) s.removeItem(key);
    else s.setItem(key, value);
  } catch {
    /* storage unavailable (private mode, blocked site data): keep working in memory */
  }
}

/* ---------------- dev users ---------------- */

const DEV_USER_KEY = 'photos.devUser';
let devUserMemory: string | null = null;

/** The dev user to act as (defaults to the seeded admin). */
export function getDevUser(): string {
  return readStore('local', DEV_USER_KEY) ?? devUserMemory ?? DEFAULT_DEV_USER;
}

export function setDevUser(id: string): void {
  devUserMemory = id;
  writeStore('local', DEV_USER_KEY, id);
}

/* ---------------- cognito ---------------- */

interface Tokens {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  /** epoch ms */
  expiresAt: number;
}

const TOKENS_KEY = 'photos.tokens';
const PKCE_KEY = 'photos.pkce';
let tokens: Tokens | null = null;
let refreshing: Promise<Tokens | null> | null = null;

function cognitoConfig() {
  // A bare host (auth.jbchusa.org, the HostedUiDomain output) or a full https:// origin.
  const host = String(env.VITE_COGNITO_DOMAIN ?? '').replace(/\/$/, '');
  const domain = host && !/^https?:\/\//.test(host) ? `https://${host}` : host;
  const clientId = String(env.VITE_COGNITO_CLIENT_ID ?? '');
  const redirectUri = String(env.VITE_REDIRECT_URI ?? `${location.origin}/auth/callback`);
  if (!domain || !clientId) throw new Error('Cognito is not configured (VITE_COGNITO_DOMAIN / VITE_COGNITO_CLIENT_ID).');
  return { domain, clientId, redirectUri };
}

function loadTokens(): Tokens | null {
  if (tokens) return tokens;
  const raw = readStore('session', TOKENS_KEY);
  if (!raw) return null;
  try {
    tokens = JSON.parse(raw) as Tokens;
    return tokens;
  } catch {
    return null;
  }
}

function saveTokens(t: Tokens | null): void {
  tokens = t;
  writeStore('session', TOKENS_KEY, t ? JSON.stringify(t) : null);
}

export function base64Url(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomString(bytes = 32): string {
  return base64Url(crypto.getRandomValues(new Uint8Array(bytes)));
}

export async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}

/** Expiry (epoch ms) from a JWT's `exp` claim. */
function jwtExpiry(jwt: string): number | null {
  const part = jwt.split('.')[1];
  if (!part) return null;
  try {
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: number };
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

/** Send the browser to the Hosted UI. Resolves never (the page navigates away). */
export async function login(returnTo = location.pathname + location.search): Promise<void> {
  const { domain, clientId, redirectUri } = cognitoConfig();
  const verifier = randomString(48);
  const state = randomString(16);
  writeStore('session', PKCE_KEY, JSON.stringify({ verifier, state, returnTo }));
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    redirect_uri: redirectUri,
    scope: 'openid email profile',
    code_challenge_method: 'S256',
    code_challenge: await pkceChallenge(verifier),
    state,
  });
  location.assign(`${domain}/oauth2/authorize?${params}`);
}

interface TokenEndpointResponse {
  id_token: string;
  access_token: string;
  refresh_token?: string;
  expires_in: number;
}

async function tokenRequest(body: Record<string, string>): Promise<TokenEndpointResponse> {
  const { domain } = cognitoConfig();
  const res = await fetch(`${domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams(body),
  });
  if (!res.ok) throw new Error(`Sign-in failed (${res.status}).`);
  return (await res.json()) as TokenEndpointResponse;
}

function toTokens(r: TokenEndpointResponse, previousRefresh?: string): Tokens {
  return {
    idToken: r.id_token,
    accessToken: r.access_token,
    refreshToken: r.refresh_token ?? previousRefresh,
    expiresAt: jwtExpiry(r.id_token) ?? Date.now() + r.expires_in * 1000,
  };
}

/** Finish the Hosted UI redirect: exchange `code` for tokens. Returns the path to go back to. */
export async function completeLogin(search: string): Promise<string> {
  const q = new URLSearchParams(search);
  const err = q.get('error_description') ?? q.get('error');
  if (err) throw new Error(err);
  const code = q.get('code');
  const saved = readStore('session', PKCE_KEY);
  writeStore('session', PKCE_KEY, null);
  if (!code || !saved) throw new Error('This sign-in link has expired. Please sign in again.');
  const { verifier, state, returnTo } = JSON.parse(saved) as { verifier: string; state: string; returnTo: string };
  if (q.get('state') !== state) throw new Error('Sign-in state did not match. Please sign in again.');
  const { clientId, redirectUri } = cognitoConfig();
  const r = await tokenRequest({
    grant_type: 'authorization_code',
    client_id: clientId,
    code,
    redirect_uri: redirectUri,
    code_verifier: verifier,
  });
  saveTokens(toTokens(r));
  return returnTo && returnTo.startsWith('/') && !returnTo.startsWith('/auth/') ? returnTo : '/';
}

async function refresh(): Promise<Tokens | null> {
  const t = loadTokens();
  if (!t?.refreshToken) return null;
  refreshing ??= (async () => {
    try {
      const r = await tokenRequest({
        grant_type: 'refresh_token',
        client_id: cognitoConfig().clientId,
        refresh_token: t.refreshToken!,
      });
      const next = toTokens(r, t.refreshToken);
      saveTokens(next);
      return next;
    } catch {
      saveTokens(null);
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

/** A valid ID token, refreshing it when it is about to expire. Null when signed out. */
export async function getIdToken(forceRefresh = false): Promise<string | null> {
  const t = loadTokens();
  if (!t) return null;
  if (!forceRefresh && t.expiresAt - 60_000 > Date.now()) return t.idToken;
  return (await refresh())?.idToken ?? null;
}

export function isSignedIn(): boolean {
  return authMode === 'dev' || !!loadTokens();
}

export function logout(): void {
  saveTokens(null);
  if (authMode !== 'cognito') return;
  const { domain, clientId } = cognitoConfig();
  location.assign(`${domain}/logout?${new URLSearchParams({ client_id: clientId, logout_uri: `${location.origin}/` })}`);
}

/** Headers that authenticate an API request. */
export async function authHeaders(forceRefresh = false): Promise<Record<string, string>> {
  if (authMode === 'dev') {
    return { 'x-dev-user': getDevUser() };
  }
  const token = await getIdToken(forceRefresh);
  return token ? { authorization: `Bearer ${token}` } : {};
}
